import React from 'react';
import { Banknote, Bell, Check, CheckCircle, FileText, Package, Trash2, User } from 'lucide-react';
import type { AppNotification } from '../../api/types';

interface Props {
  notification: AppNotification;
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClick: (notification: AppNotification) => void;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  contract_signed: <FileText size={16} />,
  event_confirmed: <CheckCircle size={16} />,
  delivery_done: <Package size={16} />,
  deposit_received: <Banknote size={16} />,
  final_payment_received: <Banknote size={16} />,
  new_client: <User size={16} />,
  collaborator_assigned: <User size={16} />,
};

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Adesso';
  if (mins < 60) return `${mins} min fa`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}g fa`;
  return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

export function NotificationItem({ notification, onMarkRead, onDelete, onClick }: Props) {
  const icon = TYPE_ICONS[notification.type] ?? <Bell size={16} />;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(notification)}
      onKeyDown={e => { if (e.key === 'Enter') onClick(notification); }}
      style={{
        display: 'flex',
        gap: '0.625rem',
        padding: '0.75rem',
        borderRadius: 'var(--radius-card)',
        cursor: 'pointer',
        background: notification.isRead ? 'transparent' : 'var(--color-primary-highlight)',
        transition: 'background var(--transition-interactive)',
        border: 'none',
        width: '100%',
        textAlign: 'left',
        fontSize: 'var(--text-sm)',
      }}
      onMouseEnter={e => { if (notification.isRead) e.currentTarget.style.background = 'var(--glass-bg-card)'; }}
      onMouseLeave={e => { if (notification.isRead) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ fontSize: '1rem', lineHeight: 1.4, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: notification.isRead ? 400 : 600,
          color: 'var(--color-text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {notification.title}
        </div>
        <div style={{
          color: 'var(--color-text-muted)',
          marginTop: '0.125rem',
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {notification.message}
        </div>
        <div style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)', marginTop: '0.25rem' }}>
          {formatRelativeTime(notification.createdAt)}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flexShrink: 0 }}>
        {!notification.isRead && (
          <button
            onClick={e => { e.stopPropagation(); onMarkRead(notification.id); }}
            aria-label="Segna come letto"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.125rem', borderRadius: 'var(--radius-interactive)' }}
          >
            <Check size={14} />
          </button>
        )}
        <button
          onClick={e => { e.stopPropagation(); onDelete(notification.id); }}
          aria-label="Elimina notifica"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.125rem', borderRadius: 'var(--radius-interactive)' }}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
